import { ChartConfig, ChartContainer } from "design-system/molecules/Chart"
import type { KeyboardEvent, MouseEvent } from "react"
import { useMemo } from "react"
import { Treemap, TreemapNode } from "recharts"
import { GetBackofficeFlagsTreemapResponseDataSchema } from "schema"
import { z } from "zod"

const chartConfig = {} satisfies ChartConfig
const MINIMUM_COMPANY_OCCURRENCES = 20

type Companies = Extract<
  z.infer<typeof GetBackofficeFlagsTreemapResponseDataSchema>,
  { groupBy: "company" }
>["companies"]

type CompanyNode = {
  companyId: string
  companyName: string
  count: number
  rate: number
  fillOpacity: number
}

export type FlagsCompanyTreemapTooltipData = Pick<
  CompanyNode,
  "companyId" | "companyName" | "count"
> & {
  rate: number
  coordinate: {
    x: number
    y: number
  }
}

type Props = {
  data: Companies
  onCompanyClick: (company: { id: string; name: string }) => void
  onTooltipChange: (tooltip: FlagsCompanyTreemapTooltipData | null) => void
}

const truncateLabel = (label: string, width: number) => {
  const maxCharacters = Math.max(Math.floor((width - 16) / 7), 0)
  if (label.length <= maxCharacters) return label
  if (maxCharacters <= 1) return ""
  return `${label.slice(0, maxCharacters - 1)}…`
}

export function BackofficeFlagsCompanyTreemapChart({
  data,
  onCompanyClick,
  onTooltipChange,
}: Props) {
  const chartData = useMemo(
    () =>
      data
        .filter((company) => company.count >= MINIMUM_COMPANY_OCCURRENCES)
        .map((company, index) => ({
          companyId: company.id,
          companyName: company.name ?? company.id,
          count: company.count,
          rate: company.rate,
          fillOpacity: Math.max(0.35, 0.85 - index * 0.05),
        })),
    [data],
  )

  return (
    <ChartContainer config={chartConfig} className="h-[280px] w-full">
      <Treemap
        data={chartData}
        dataKey="rate"
        nameKey="companyName"
        content={<CompanyTreemapContent onCompanyClick={onCompanyClick} />}
        onMouseEnter={(node, event) => {
          const company = node as TreemapNode & Partial<CompanyNode>
          if (!company.companyId || !company.companyName) return

          const mouseEvent = event as MouseEvent<SVGElement>
          onTooltipChange({
            companyId: company.companyId,
            companyName: company.companyName,
            count: company.count ?? 0,
            rate: company.rate ?? 0,
            coordinate: { x: mouseEvent.clientX + 12, y: mouseEvent.clientY + 12 },
          })
        }}
        onMouseLeave={() => onTooltipChange(null)}
        isAnimationActive={false}
      />
    </ChartContainer>
  )
}

type ContentProps = Partial<TreemapNode> & {
  onCompanyClick: (company: { id: string; name: string }) => void
}

function CompanyTreemapContent({ onCompanyClick, ...props }: ContentProps) {
  const { x = 0, y = 0, width = 0, height = 0, depth = 0 } = props
  const company = props as CompanyNode
  if (depth === 0 || !company.companyId) return null

  const label = truncateLabel(company.companyName, width)
  const onKeyDown = (event: KeyboardEvent<SVGGElement>) => {
    if (event.key !== "Enter" && event.key !== " ") return
    event.preventDefault()
    onCompanyClick({ id: company.companyId, name: company.companyName })
  }

  return (
    <g
      role="button"
      tabIndex={0}
      aria-label={company.companyName}
      className="group cursor-pointer"
      onClick={() => onCompanyClick({ id: company.companyId, name: company.companyName })}
      onKeyDown={onKeyDown}
    >
      <rect
        x={x + 2}
        y={y + 2}
        width={Math.max(width - 4, 0)}
        height={Math.max(height - 4, 0)}
        fill="var(--warning)"
        fillOpacity={company.fillOpacity}
        stroke="var(--background)"
        strokeWidth={1}
        rx={3}
        className="opacity-60 transition-opacity group-hover:opacity-100
          group-focus-visible:opacity-100"
      />
      {label && height >= 36 && (
        <text x={x + 12} y={y + 24} fill="var(--foreground)" fontSize={12} fontWeight={500}>
          {label}
        </text>
      )}
    </g>
  )
}
